import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import client from "./lib/api";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import PaymentResult from "./pages/PaymentResult";
import PhotographerLanding from "./pages/PhotographerLanding";
import AgentReferralLanding from "./pages/AgentReferralLanding";
import PayoutReturn from "./pages/PayoutReturn";
import BookingWidget from "./pages/BookingWidget";
import LpgDemo from "./pages/LpgDemo";
import LeadLander from "./pages/LeadLander";
import Privacy from "./pages/Privacy";
import { initConsent } from "./lib/analytics";
import { BrandMark } from "./components/BrandMark";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-black text-2xl text-[#4A0000] tracking-tight">REELTY</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Handles the Google OAuth return: exchanges the session_id (URL fragment) for our app token.
function AuthCallback() {
  const { login } = useAuth();
  const nav = useNavigate();
  const ran = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sid) { setErr("Sign-in was cancelled."); return; }
    const signup = sessionStorage.getItem("reelty_signup") === "1";
    sessionStorage.removeItem("reelty_signup");
    client.post("/auth/google/session", { session_id: sid, signup })
      .then((r) => { login(r.data.token, r.data.user); nav("/", { replace: true }); })
      .catch((e) => setErr(e.response?.data?.detail || "Google sign-in failed."));
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7]" data-testid="auth-callback">
      <div className="text-center px-6">
        <div className="flex justify-center mb-3"><BrandMark logoSize={42} textClass="text-[#4A0000]" textSize="text-3xl" /></div>
        {err ? (
          <>
            <p className="text-[#B4472E]">{err}</p>
            <button onClick={() => nav("/login", { replace: true })} className="mt-4 text-[#4A0000] underline">Back to sign in</button>
          </>
        ) : <p className="text-[#5C5A58]">Signing you in…</p>}
      </div>
    </div>
  );
}

function AppInner() {
  const location = useLocation();
  // Detect the OAuth return synchronously (before any protected route runs).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/p/:slug" element={<PhotographerLanding />} />
      <Route path="/book" element={<BookingWidget />} />
      <Route path="/lpg-demo" element={<LpgDemo />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/property/:id" element={<LeadLander />} />
      <Route path="/r/:slug" element={<AgentReferralLanding />} />
      <Route path="/a/:slug" element={<AgentReferralLanding />} />
      <Route path="/x/:slug" element={<AgentReferralLanding />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult cancel />} />
      <Route path="/payouts/return" element={<Protected><PayoutReturn /></Protected>} />
      <Route path="/payouts/refresh" element={<Protected><PayoutReturn /></Protected>} />
      <Route path="/*" element={<Protected><Dashboard /></Protected>} />
    </Routes>
  );
}

function App() {
  useEffect(() => { initConsent(); }, []);
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
